/**
 * Order success confirmation card component
 */

import { useState } from 'react'
import { Button } from '@/components/ui'
import { CheckCircle2, Clock, Copy, Package } from 'lucide-react'

interface OrderSuccessCardProps {
  orderNumber: string
  estimatedTime?: string | null
  trackingCode?: string | null
  onBackToStore: () => void
  onTrackOrder?: () => void
}

export function OrderSuccessCard({
  orderNumber,
  estimatedTime,
  trackingCode,
  onBackToStore,
  onTrackOrder,
}: OrderSuccessCardProps) {
  const [linkCopied, setLinkCopied] = useState(false)

  const handleCopyTrackingLink = () => {
    if (!trackingCode) return
    const trackingUrl = `${window.location.origin}/tracking/${trackingCode}`
    navigator.clipboard.writeText(trackingUrl).then(() => {
      setLinkCopied(true)
      setTimeout(() => setLinkCopied(false), 2000)
    })
  }

  return (
    <div className="border rounded-xl p-8 text-center space-y-6 bg-primary/5 border-primary/20">
      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10">
        <CheckCircle2 className="h-12 w-12 text-primary" />
      </div>

      <div>
        <h3 className="font-bold text-2xl mb-2">Order Placed Successfully!</h3>
        <p className="text-sm text-muted-foreground">
          Your order is being prepared!
        </p>
      </div>

      <div className="bg-background border rounded-xl p-5 inline-block">
        <div className="text-xs text-muted-foreground mb-1">Order Number</div>
        <div className="font-mono font-bold text-2xl">{orderNumber}</div>
      </div>

      {estimatedTime && (
        <div className="flex items-center justify-center gap-2 text-sm">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground">
            Estimated time:{' '}
            <span className="font-medium text-foreground">
              {new Date(estimatedTime).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </span>
        </div>
      )}

      {trackingCode && (
        <div className="bg-background border rounded-xl p-3 text-left space-y-2">
          <div className="text-xs text-muted-foreground">Tracking link</div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs text-foreground truncate flex-1">
              {window.location.origin}/tracking/{trackingCode}
            </span>
            <Button
              variant="outline"
              size="sm"
              className="shrink-0 gap-1.5"
              onClick={handleCopyTrackingLink}
            >
              <Copy className="h-3.5 w-3.5" />
              {linkCopied ? 'Copied!' : 'Copy'}
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {onTrackOrder && (
          <Button variant="black" className="w-full gap-2" onClick={onTrackOrder}>
            <Package className="h-4 w-4" />
            Track Order
          </Button>
        )}
        <Button
          variant={onTrackOrder ? 'outline' : 'black'}
          className="w-full"
          onClick={onBackToStore}
        >
          Back to Store
        </Button>
      </div>
    </div>
  )
}
